import { defaultDescriptionTemplate } from '@mesa/core/config';
import { type BackfillStatus, type BackfillStep } from '@mesa/core/types';
import { defineStepper } from '@stepperize/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowRightIcon, CheckCircle2Icon, CircleIcon, Loader2Icon, ZapIcon, DownloadIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import mesaLogo from '@/assets/logo-bw-transparent.svg';
import approachingMesa from '@/assets/onboarding-2.webp';
import { MarkdownEditor } from '@/components/editor/markdown-editor';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useZero, useZeroQuery } from '@/lib/zero';
import { Installation, useCurrentOrg } from '@/services/org';

export const Route = createFileRoute('/_authed/onboarding/$owner')({
  // params: { parse: (params) => z.object({ owner: z.string() }).parse(params) },
  component: OnboardingPage,
});

const Stepper = defineStepper(
  {
    id: '0',
    title: 'Pull Settings',
    description: 'Configure how Mesa should enrich your PRs.',
  },
  {
    id: '1',
    title: 'Review Agent: Triggers',
    description: 'Choose how and when Mesa reviews your PRs.',
  },
  {
    id: '2',
    title: 'Review Agent: Config',
    description: 'Choose what Mesa should look for when reviewing your code.',
  },
  {
    id: '3',
    title: 'Review Agent: Auto Approvals',
    description: 'Choose when Mesa should automatically approve your PRs.',
  },
  {
    id: '4',
    title: 'Account Setup',
    description: 'Syncing pull requests from GitHub and setting up your account.',
  }
);

function OnboardingPage() {
  const { currentOrg } = useCurrentOrg();
  const { isLast, next, all, current, goTo } = Stepper.useStepper();
  const navigate = useNavigate();
  const zero = useZero();
  // We need this line here. It's not clear why, but without it, our custom mutators cannot properly read the existing record
  const [currentOrgConfig] = useZeroQuery(zero.query.OrgConfigTable.where('owner', currentOrg?.account.login).one());
  const [backfillStatus] = useZeroQuery(zero.query.BackfillStatusTable.where('installationId', currentOrg?.id).one());

  const backfillingOpenPulls = useMemo(
    () => backfillStatus?.steps?.some((step) => step.step.includes('open') && step.status === 'running'),
    [backfillStatus]
  );

  if (!currentOrg) {
    console.error('No current org');
    navigate({ to: '/pulls' });
    return null;
  }

  return (
    <div
      className="flex items-center justify-start h-full"
      style={{
        backgroundImage: `url('${approachingMesa}')`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundColor: '#000',
      }}
    >
      <Stepper.Scoped>
        <div className="flex flex-col w-full max-w-[694px] h-full max-h-screen bg-stone-50">
          <div className="flex gap-2 items-center justify-between p-4 flex-shrink-0 h-fit">
            <div className="flex items-baseline gap-2">
              <img src={mesaLogo} alt="Mesa Valley" className="size-6" />
              <h2 className="text-2xl font-bold text-stone-700">Mesa</h2>
            </div>
            <h3 className="text-md mr-8 text-stone-500">{current.title}</h3>
          </div>
          <div className="flex flex-col gap-1 px-8 py-4 flex-1 overflow-y-auto">
            {/* <p className="text-sm text-stone-500 w-full text-center mb-4">{current.description}</p> */}
            {current.id === '0' && <PullSettingsForm currentOrg={currentOrg} />}
            {current.id === '1' && <ReviewAgentTriggersForm currentOrg={currentOrg} />}
            {current.id === '2' && <ReviewAgentConfigForm currentOrg={currentOrg} />}
            {current.id === '3' && <ReviewAgentAutoApprovalsForm currentOrg={currentOrg} />}
            {current.id === '4' && <BackfillStatus backfillStatus={backfillStatus as BackfillStatus | undefined} />}
          </div>
          <div className="flex gap-4 items-center justify-between px-8 py-4 flex-shrink-0">
            <div className="flex flex-col w-full gap-2 items-center h-full max-w-[498px]">
              {backfillStatus && !backfillingOpenPulls && backfillStatus.status === 'running' && (
                <p className="text-xs text-emerald-600">
                  You're ready to go! Background sync continues while you use Mesa.
                </p>
              )}
              <div className="flex w-full gap-2 items-center h-full">
                {all.map((step) => {
                  const isCurrent = step.id === current.id;
                  const isCompleted = step.id < current.id;
                  return (
                    <Tooltip key={step.id}>
                      <TooltipTrigger asChild className="w-full">
                        <div>
                          <Progress
                            value={isCompleted ? 100 : 0}
                            className={cn(
                              'h-1.5 transition-all duration-100',
                              'hover:h-3 hover:bg-primary/30 hover:cursor-pointer',
                              isCurrent && 'border border-foreground'
                            )}
                            onClick={() => goTo(step.id)}
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>{step.title}</TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
            {isLast ? (
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white w-20 flex items-center justify-center gap-1"
                onClick={() => navigate({ to: '/pulls' })}
                disabled={backfillingOpenPulls || backfillStatus?.status === 'waiting'}
              >
                Enter
                <ArrowRightIcon className="size-4 text-white" />
              </Button>
            ) : (
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white w-20 flex items-center justify-center gap-1"
                onClick={() => (isLast ? navigate({ to: '/pulls' }) : next())}
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </Stepper.Scoped>
    </div>
  );
}

function PullSettingsForm({ currentOrg }: { currentOrg: Installation }) {
  const [selectedValue, setSelectedValue] = useState('enabled');
  const [descriptionTemplate, setDescriptionTemplate] = useState(defaultDescriptionTemplate);
  const automaticDescription = {
    enabled: {
      value: 'enabled',
      label: 'Enabled',
      description:
        'Mesa will generate a description for the PR when it is opened or published from draft. If Mesa detects a description is already written, it will not override it.',
    },
    disabled: {
      value: 'disabled',
      label: 'Disabled',
      description:
        'Mesa will not automatically generate a description for your PR. You can manually generate by tagging `@mesa-dot-dev describe` in your pull request.',
    },
  };
  const zero = useZero();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label className="text-lg font-medium">Automatic Descriptions</Label>
        <RadioGroup
          value={selectedValue}
          onValueChange={(value) => {
            setSelectedValue(value);
            zero.mutate.OrgConfigTable.update({
              owner: currentOrg.account.login,
              key: ['pulls', 'description', 'autoGenerate'],
              value: value === 'enabled',
            });
          }}
        >
          <div className="flex flex-col gap-2">
            {Object.entries(automaticDescription).map(([key, value]) => (
              <Label
                key={key}
                htmlFor={value.value}
                className={cn(
                  'radio-tile',
                  selectedValue === key
                    ? key === 'enabled'
                      ? 'radio-tile-selected-emerald'
                      : 'radio-tile-selected-orange'
                    : 'radio-tile-unselected'
                )}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value={value.value} id={value.value} className="sr-only" />
                  <span className="font-medium text-base">{value.label}</span>
                </div>
                <span className="text-sm text-muted-foreground font-normal">{value.description}</span>
              </Label>
            ))}
          </div>
        </RadioGroup>

        {selectedValue === 'enabled' && (
          <div className="flex flex-col gap-2 mt-4">
            <Label htmlFor="description-template" className="text-base font-medium">
              Description Template
            </Label>
            <MarkdownEditor
              owner={currentOrg.account.login}
              key="template-editor"
              value={descriptionTemplate}
              onBlur={({ editor }) => {
                setDescriptionTemplate(editor.storage.markdown.getMarkdown());
                zero.mutate.OrgConfigTable.update({
                  owner: currentOrg.account.login,
                  key: ['pulls', 'description', 'template'],
                  value: editor.storage.markdown.getMarkdown(),
                });
              }}
              placeholder="Add a pull request description template..."
              className="inset-shadow-sm bg-stone-50/75 rounded-lg p-2 min-h-[300px]"
              showBubbleMenu
            />
          </div>
        )}
      </div>
    </div>
  );
}

function ReviewAgentTriggersForm({ currentOrg }: { currentOrg: Installation }) {
  const [autoReview, setAutoReview] = useState(true);
  const [syncComments, setSyncComments] = useState(true);
  const zero = useZero();

  const triggerSettings = [
    {
      label: 'Automatically Review New PRs',
      description:
        'Mesa will automatically review pull requests when they are opened, published from draft, or when new commits are pushed.',
      checked: autoReview,
      onCheckedChange: () => {
        setAutoReview(!autoReview);
        zero.mutate.OrgConfigTable.update({
          owner: currentOrg.account.login,
          key: ['reviewAgent', 'autoReview'],
          value: !autoReview,
        });
      },
    },
    {
      label: 'Sync Comments to GitHub',
      description:
        'When disabled, Mesa will still perform and post reviews to GitHub, but inline review comments will not by posted to GitHub. You can always view all comments in the Mesa UI.',
      checked: syncComments,
      onCheckedChange: () => {
        setSyncComments(!syncComments);
        zero.mutate.OrgConfigTable.update({
          owner: currentOrg.account.login,
          key: ['reviewAgent', 'syncCommentsToGitHub'],
          value: !syncComments,
        });
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4">
        {triggerSettings.map((setting) => (
          <Label
            key={setting.label}
            htmlFor={setting.label}
            className="flex items-center justify-between p-4 border rounded-lg bg-stone-50/75 text-base font-medium cursor-pointer gap-8"
          >
            <div className="flex flex-col gap-1">
              <p className="font-medium">{setting.label}</p>
              <p className="text-sm text-muted-foreground font-normal">{setting.description}</p>
            </div>
            <Switch id={setting.label} checked={setting.checked} onCheckedChange={setting.onCheckedChange} />
          </Label>
        ))}
      </div>
    </div>
  );
}

function ReviewAgentConfigForm({ currentOrg }: { currentOrg: Installation }) {
  const [commentTypes, setCommentTypes] = useState({
    Logic: true,
    Style: true,
    Security: true,
    Performance: true,
    Question: false,
    Other: false,
  });
  const [granularity, setGranularity] = useState('high-signal');
  const zero = useZero();

  const commentTypeLabels = {
    Logic: { label: 'Logic Issues', description: 'Bugs, edge cases, and logical errors' },
    Style: { label: 'Style', description: 'Code style and patterns' },
    Security: { label: 'Security', description: 'Security vulnerabilities and unsafe code' },
    Performance: { label: 'Performance', description: 'N+1 queries, performance footguns, and issues' },
    Question: { label: 'Questions', description: 'Clarifying questions about the code' },
    Other: { label: 'Other', description: 'Miscellaneous comments and suggestions' },
  };

  const granularityOptions = {
    'high-signal': {
      value: 'high-signal',
      label: 'High Signal',
      description: 'Comment on bigger issues that pose a risk',
    },
    detailed: {
      value: 'detailed',
      label: 'Detailed',
      description: 'Comment on all issues, including low severity suggestions for improving the code',
    },
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <Label className="text-lg font-medium">Review Granularity</Label>
        <RadioGroup
          value={granularity}
          onValueChange={(value) => {
            setGranularity(value);
            zero.mutate.OrgConfigTable.update({
              owner: currentOrg.account.login,
              key: ['reviewAgent', 'commentSeverities'],
              value:
                value === 'high-signal'
                  ? { High: true, Medium: true, Low: false }
                  : { High: true, Medium: true, Low: true },
            });
          }}
        >
          <div className="flex flex-col gap-2">
            {Object.entries(granularityOptions).map(([key, value]) => (
              <Label
                key={key}
                htmlFor={value.value}
                className={cn(
                  'radio-tile',
                  granularity === key ? 'radio-tile-selected-emerald' : 'radio-tile-unselected'
                )}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value={value.value} id={value.value} className="sr-only" />
                  <span className="font-medium text-sm">{value.label}</span>
                </div>
                <span className="text-sm text-muted-foreground font-normal">{value.description}</span>
              </Label>
            ))}
          </div>
        </RadioGroup>
      </div>
      <div className="flex flex-col gap-3">
        <Label className="text-lg font-medium">Comment Types</Label>
        <p className="text-sm text-muted-foreground">Select which types of comments Mesa should include in reviews</p>
        <div className="grid grid-cols-1 gap-3">
          {Object.entries(commentTypeLabels).map(([key, config]) => (
            <div key={key} className="flex items-center space-x-3 p-3 border rounded-lg bg-stone-50/75">
              <Checkbox
                id={key}
                checked={commentTypes[key as keyof typeof commentTypes]}
                onCheckedChange={(checked) => {
                  setCommentTypes((prev) => ({ ...prev, [key]: checked as boolean }));
                  zero.mutate.OrgConfigTable.update({
                    owner: currentOrg.account.login,
                    key: ['reviewAgent', 'commentTypes', key],
                    value: checked,
                  });
                }}
                className=""
              />
              <div className="flex flex-col">
                <Label htmlFor={key} className="text-sm font-medium cursor-pointer flex flex-col items-start gap-0">
                  <p className="font-medium">{config.label}</p>
                  <p className="text-xs text-muted-foreground">{config.description}</p>
                </Label>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ReviewAgentAutoApprovalsForm({ currentOrg }: { currentOrg: Installation }) {
  const [enableAutoApprovals, setEnableAutoApprovals] = useState(true);
  const [labels, setLabels] = useState<string[]>(['minor-fix']);
  const [labelInput, setLabelInput] = useState('');
  const [thresholds, setThresholds] = useState({
    low: undefined as number | undefined,
    medium: 1 as number | undefined,
    high: 0 as number | undefined,
  });
  const zero = useZero();

  const handleAddLabel = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && labelInput.trim()) {
      e.preventDefault();
      if (!labels.includes(labelInput.trim())) {
        zero.mutate.OrgConfigTable.update({
          owner: currentOrg.account.login,
          key: ['reviewAgent', 'autoApprove', 'labels'],
          value: [...labels, labelInput.trim()],
        });
        setLabels([...labels, labelInput.trim()]);
      }
      setLabelInput('');
    }
  };

  const removeLabel = (labelToRemove: string) => {
    zero.mutate.OrgConfigTable.update({
      owner: currentOrg.account.login,
      key: ['reviewAgent', 'autoApprove', 'labels'],
      value: labels.filter((label) => label !== labelToRemove),
    });
    setLabels(labels.filter((label) => label !== labelToRemove));
  };

  return (
    <div className="flex flex-col gap-4">
      <Label
        htmlFor="enable-auto-approvals"
        className="flex items-center justify-between p-4 border rounded-lg bg-stone-50/75 text-base font-medium cursor-pointer gap-0"
      >
        <div className="flex flex-col gap-1">
          <p className="font-medium">Enable Auto-Approvals</p>
          <p className="text-sm text-muted-foreground font-normal">
            Automatically approve PRs that meet your defined criteria
          </p>
        </div>
        <Switch
          id="enable-auto-approvals"
          checked={enableAutoApprovals}
          onCheckedChange={() => {
            zero.mutate.OrgConfigTable.update({
              owner: currentOrg.account.login,
              key: ['reviewAgent', 'autoApprove', 'enabled'],
              value: !enableAutoApprovals,
            });
            setEnableAutoApprovals(!enableAutoApprovals);
          }}
        />
      </Label>

      {enableAutoApprovals && (
        <div className="flex flex-col gap-6 mt-2">
          <div className="flex flex-col gap-3">
            <Label className="text-base font-medium">Trigger Labels</Label>
            <p className="text-sm text-muted-foreground">PRs with these labels will be enabled for auto-approval.</p>
            <div className="flex flex-col gap-2">
              {labels.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {labels.map((label) => (
                    <Badge
                      key={label}
                      variant="secondary"
                      className="cursor-pointer hover:bg-destructive/20 hover:text-destructive-foreground bg-sky-800/20"
                      onClick={() => removeLabel(label)}
                    >
                      {label}
                      <span className="ml-1">×</span>
                    </Badge>
                  ))}
                </div>
              )}
              <Input
                placeholder="Add a label (press Enter)"
                value={labelInput}
                onChange={(e) => setLabelInput(e.target.value)}
                onKeyDown={handleAddLabel}
                className="bg-stone-50/75"
              />
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Label className="text-base font-medium">Approval Thresholds</Label>
            <p className="text-sm text-muted-foreground">
              PRs with more issues than the below thresholds will not be auto-approved
            </p>
            <div className="grid grid-cols-1 gap-3">
              <div className="flex items-center justify-between p-3 border rounded-lg bg-stone-50/75">
                <div className="flex flex-col gap-0.5">
                  <Label htmlFor="low-threshold" className="text-sm font-medium">
                    Low Severity
                  </Label>
                  <p className="text-xs text-muted-foreground">Style suggestions and minor improvements</p>
                </div>
                <Input
                  id="low-threshold"
                  type="number"
                  min="0"
                  value={thresholds.low ?? ''}
                  onChange={(e) => {
                    zero.mutate.OrgConfigTable.update({
                      owner: currentOrg.account.login,
                      key: ['reviewAgent', 'autoApprove', 'threshold', 'low'],
                      value: e.target.value ? parseInt(e.target.value) : undefined,
                    });
                    setThresholds((prev) => ({
                      ...prev,
                      low: e.target.value ? parseInt(e.target.value) : undefined,
                    }));
                  }}
                  placeholder="No limit"
                  className="w-24 bg-stone-50/75"
                />
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg bg-stone-50/75">
                <div className="flex flex-col gap-0.5">
                  <Label htmlFor="medium-threshold" className="text-sm font-medium">
                    Medium Severity
                  </Label>
                  <p className="text-xs text-muted-foreground">Potential bugs and code quality issues</p>
                </div>
                <Input
                  id="medium-threshold"
                  type="number"
                  min="0"
                  value={thresholds.medium ?? ''}
                  onChange={(e) => {
                    zero.mutate.OrgConfigTable.update({
                      owner: currentOrg.account.login,
                      key: ['reviewAgent', 'autoApprove', 'threshold', 'medium'],
                      value: e.target.value ? parseInt(e.target.value) : undefined,
                    });
                    setThresholds((prev) => ({
                      ...prev,
                      medium: e.target.value ? parseInt(e.target.value) : undefined,
                    }));
                  }}
                  placeholder="No limit"
                  className="w-24 bg-stone-50/75"
                />
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg bg-stone-50/75">
                <div className="flex flex-col gap-0.5">
                  <Label htmlFor="high-threshold" className="text-sm font-medium">
                    High Severity
                  </Label>
                  <p className="text-xs text-muted-foreground">Critical bugs and security issues</p>
                </div>
                <Input
                  id="high-threshold"
                  type="number"
                  min="0"
                  value={thresholds.high ?? ''}
                  onChange={(e) => {
                    zero.mutate.OrgConfigTable.update({
                      owner: currentOrg.account.login,
                      key: ['reviewAgent', 'autoApprove', 'threshold', 'high'],
                      value: e.target.value ? parseInt(e.target.value) : undefined,
                    });
                    setThresholds((prev) => ({
                      ...prev,
                      high: e.target.value ? parseInt(e.target.value) : undefined,
                    }));
                  }}
                  placeholder="No limit"
                  className="w-24 bg-stone-50/75"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BackfillStatus({ backfillStatus }: { backfillStatus: BackfillStatus | undefined }) {
  const openSteps = backfillStatus?.steps?.filter((step) => step.step.includes('open')) || [];
  const canProceed =
    backfillStatus?.status === 'running' &&
    openSteps.length > 0 &&
    openSteps.every((step) => step.status === 'completed');

  return (
    <div className="flex flex-col gap-4 mt-4">
      {/* Progress Summary */}
      <div className="flex flex-col gap-3">
        <Label className="text-base font-medium">Repository Sync</Label>
        <p className="text-sm text-muted-foreground">Syncing pull requests from Github</p>
      </div>

      {/* Ready to proceed message */}
      {canProceed && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle2Icon className="size-5 text-emerald-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-emerald-900">Ready to get started!</h4>
              <p className="text-sm text-emerald-700 mt-1">
                Your open pull requests are synced. Loading older data will continue while you explore Mesa.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Waiting state */}
      {(!backfillStatus || backfillStatus.status === 'waiting') && (
        <div className="flex items-center justify-center h-full gap-2">
          <span>Waiting for backfill worker to start...</span>
          <Loader2Icon className="h-4 w-4 animate-spin" />
        </div>
      )}

      {/* Running state with categorized steps */}
      {backfillStatus && backfillStatus.status === 'running' && (
        <div className="flex flex-col h-full gap-6">
          {/* Essential Steps */}
          {backfillStatus.steps.filter((step) => step.step.includes('open')).length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                <ZapIcon className="size-4 text-emerald-500" />
                Getting Started
              </h3>
              <div className="space-y-3">
                {backfillStatus.steps
                  .filter((step) => step.step.includes('open'))
                  .map((step) => (
                    <BackfillStep key={step.step} step={step} />
                  ))}
              </div>
            </div>
          )}

          {/* Background Steps */}
          {backfillStatus.steps.filter((step) => !step.step.includes('open')).length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <DownloadIcon className="size-4 text-blue-500" />
                Background Sync
              </h3>
              <div className="space-y-3">
                {backfillStatus.steps
                  .filter((step) => !step.step.includes('open'))
                  .map((step) => (
                    <BackfillStep key={step.step} step={step} />
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Completed state */}
      {backfillStatus && backfillStatus.status === 'completed' && (
        <div className="flex items-center justify-center h-full gap-2">
          <CheckCircle2Icon className="h-5 w-5 text-green-500" />
          <span className="text-green-600 font-medium">Sync completed successfully!</span>
        </div>
      )}
    </div>
  );
}

const BackfillStep = ({ step }: { step: BackfillStep }) => {
  const isEssential = step.step.includes('open');

  return (
    <div className={cn('flex items-center transition-all duration-300', !isEssential && 'opacity-70')}>
      <div className="mr-4">
        {step.status === 'completed' ? (
          <CheckCircle2Icon
            className={cn('size-4 transition-all duration-300', isEssential ? 'text-emerald-500' : 'text-blue-500')}
          />
        ) : step.status === 'running' ? (
          <Loader2Icon className={cn('size-4 animate-spin', isEssential ? 'text-emerald-500' : 'text-blue-500')} />
        ) : (
          <CircleIcon className="size-4 text-gray-300" />
        )}
      </div>
      <div
        className={cn(
          'flex-1 text-sm',
          step.status === 'completed'
            ? 'text-gray-500'
            : step.status === 'running'
              ? isEssential
                ? 'font-medium text-foreground'
                : 'text-muted-foreground'
              : 'text-gray-400'
        )}
      >
        {step.label}
        {step.status === 'running' && step.progress && (
          <span className="text-gray-500 text-xs ml-2">
            ({step.progress.current} / {step.progress.total})
          </span>
        )}
      </div>
    </div>
  );
};
